import { colors, gradients, glows, radius, spacing } from './tokens';

export type ThemeMode = 'dark' | 'light';

/**
 * Maps color token keys to CSS custom property names.
 */
const colorVarMap: Record<keyof typeof colors.dark, string> = {
  bg: '--bg',
  bg2: '--bg2',
  surface: '--surface',
  surface2: '--surface2',
  surface3: '--surface3',
  text1: '--text1',
  text2: '--text2',
  text3: '--text3',
  primary: '--pri',
  primary2: '--pri2',
  cyan: '--cyan',
  pink: '--pink',
  green: '--green',
  red: '--red',
  yellow: '--yellow',
  blue: '--blue',
  border: '--border',
};

/**
 * Applies the given theme mode's design tokens as CSS custom properties
 * on `document.documentElement`, enabling theme-aware styling via `var(--token)`.
 */
export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  const palette = colors[mode];

  // Apply color tokens
  for (const [key, cssVar] of Object.entries(colorVarMap)) {
    root.style.setProperty(cssVar, palette[key as keyof typeof palette]);
  }

  // Apply gradient tokens
  root.style.setProperty('--grad-primary', gradients.primary);
  root.style.setProperty('--grad-aurora', gradients.aurora);
  root.style.setProperty('--grad-user-msg', gradients.userMessage);

  // Apply glow tokens
  root.style.setProperty('--glow-pri', glows.primary);
  root.style.setProperty('--glow-cyan', glows.cyan);
  root.style.setProperty('--glow-pink', glows.pink);
  root.style.setProperty('--glow-neon', glows.neonBreath);

  // Apply radius tokens
  root.style.setProperty('--radius-sm', radius.sm);
  root.style.setProperty('--radius-md', radius.md);
  root.style.setProperty('--radius-lg', radius.lg);
  root.style.setProperty('--radius-xl', radius.xl);
  root.style.setProperty('--radius-2xl', radius['2xl']);
  root.style.setProperty('--radius-full', radius.full);

  // Apply spacing tokens
  root.style.setProperty('--sp-xs', spacing.xs);
  root.style.setProperty('--sp-sm', spacing.sm);
  root.style.setProperty('--sp-md', spacing.md);
  root.style.setProperty('--sp-lg', spacing.lg);
  root.style.setProperty('--sp-xl', spacing.xl);
  root.style.setProperty('--sp-2xl', spacing['2xl']);
  root.style.setProperty('--sp-3xl', spacing['3xl']);
  root.style.setProperty('--sp-4xl', spacing['4xl']);

  // Set data-theme attribute for CSS selectors
  root.setAttribute('data-theme', mode);
}
